import { DataSourceFieldError, DataSourceFieldSchema } from '../models/data-source.model';

/**
 * Parse a backend validation error message into structured field errors.
 *
 * Backend format: "s3.s3_bucket: Field required. Received: {...}\ns3.s3_region: Field required. Received: {...}"
 * Strips the "Received:" noise, removes connector prefixes, and resolves
 * field keys to human-readable labels from the schema.
 */
export function parseValidationErrors(message: string, schema: DataSourceFieldSchema[]): DataSourceFieldError[] {
  return message
    .split('\n')
    .filter((line) => line.includes(': '))
    .map((line) => parseSingleErrorLine(line, schema))
    .filter((e): e is DataSourceFieldError => e !== null);
}

function parseSingleErrorLine(line: string, schema: DataSourceFieldSchema[]): DataSourceFieldError | null {
  const cleaned = stripReceivedSuffix(line);
  const colonIdx = cleaned.indexOf(': ');
  if (colonIdx === -1) return null;

  const fieldPath = cleaned.substring(0, colonIdx).trim();
  const errorMsg = cleaned.substring(colonIdx + 2).trim();
  const fieldKey = extractFieldKey(fieldPath);
  const label = resolveFieldLabel(fieldKey, schema);

  return { field: fieldKey, label, message: errorMsg };
}

function stripReceivedSuffix(line: string): string {
  const idx = line.indexOf('. Received:');
  return idx > -1 ? line.substring(0, idx) : line;
}

function extractFieldKey(fieldPath: string): string {
  const dotIdx = fieldPath.lastIndexOf('.');
  return dotIdx > -1 ? fieldPath.substring(dotIdx + 1) : fieldPath;
}

function resolveFieldLabel(fieldKey: string, schema: DataSourceFieldSchema[]): string {
  const schemaField = schema.find((f) => f.key === fieldKey);
  return schemaField?.title || humanizeFieldKey(fieldKey);
}

function humanizeFieldKey(key: string): string {
  return key
    .replace(/^(s3_|sftp_|sp_|azure_)/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
